import Alpine from 'alpinejs';
import {
    createAuthenticatedReadRecovery,
    handleAuthenticationRequiredResponse,
    readJsonResponse
} from '../../utils/authResponse.js';
import { getCsrfToken } from '../../utils/helpers.js';

Alpine.store('wishlistModal', {
    isOpen: false,
    listingId: null,
    pendingWishlists: [], // Array of UIDs
    wishlists: [], // Loaded from API or preloaded from user fetch
    newCollectionName: '',
    isSaving: false,
    isLoading: false,
    isLoaded: false, // Set by user.js after preloading
    error: '',
    // Read recovery is armed only after the API confirms that authentication
    // expired, so unrelated login events cannot trigger surprise requests.
    loginRecovery: null,
    // A failed save may restore its local input once, but it is never replayed.
    retryListingId: null,

    get creatingNewList() {
        return this.newCollectionName.trim().length > 0;
    },

    init() {
        this.initLoginRecovery();

        // Legacy: Initial load of wishlists if available from window
        if (window.avari && window.avari.wishlists) {
            this.setWishlists(window.avari.wishlists);
            this.isLoaded = true;
        }
    },

    initLoginRecovery() {
        if (this.loginRecovery) return;

        const userStore = Alpine.store('user');
        this.loginRecovery = createAuthenticatedReadRecovery({
            refreshUser: () => userStore.fetchUser(),
            isLoggedIn: () => userStore.isLoggedIn,
            refetch: () => this.loadWishlists()
        });

        window.htmx?.on('loginSucceeded', () =>
            this.loginRecovery.handleLogin()
        );
    },

    setWishlists(wishlists) {
        const lists = Array.isArray(wishlists) ? wishlists : [];
        this.wishlists = lists.map((list) => ({
            ...list,
            covers: Array.isArray(list?.covers)
                ? list.covers.filter(Boolean)
                : [],
            listingIds: Array.isArray(list?.listingIds) ? list.listingIds : []
        }));
    },

    open(listingId) {
        const normalizedListingId = parseInt(listingId, 10);
        // Reopening the same listing after reauthentication keeps the user's
        // selections; opening any other listing starts from server state.
        const preservePendingInput =
            this.retryListingId === normalizedListingId;

        this.listingId = normalizedListingId;
        this.retryListingId = null;
        this.error = '';

        if (!preservePendingInput) {
            this.newCollectionName = '';
        }

        // If already loaded, just set pending wishlists
        if (preservePendingInput) {
            if (!this.isLoaded) {
                this.loadWishlists(true);
            }
        } else if (this.isLoaded) {
            this.pendingWishlists = this.wishlists
                .filter((list) =>
                    list.listingIds.includes(parseInt(this.listingId, 10))
                )
                .map((list) => list.uid);
        } else {
            // Fetch if not preloaded
            this.loadWishlists();
        }

        // Wishlist actions can originate inside another dialog (for example,
        // the listing gallery). Clear the modal stack before opening this one
        // so backdrops and focus traps never overlap.
        const modals = Alpine.store('modals');
        if (modals) {
            modals.replace('add-to-wishlist-modal');
        }
    },

    async loadWishlists(preservePendingInput = false) {
        if (this.isLoading) return;
        this.isLoading = true;
        this.error = '';

        try {
            const response = await fetch('/actions/avari/wishlist/index', {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    Accept: 'application/json'
                },
                credentials: 'same-origin'
            });
            const data = await readJsonResponse(response);

            if (
                handleAuthenticationRequiredResponse(response, data, {
                    userStore: Alpine.store('user'),
                    modalsStore: Alpine.store('modals')
                })
            ) {
                // Reads are safe to retry once after login; writes are handled
                // separately in save() and always require user confirmation.
                this.isLoaded = false;
                this.loginRecovery?.request();
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }

            if (!data) {
                throw new Error('Wishlist API returned an invalid response');
            }

            if (data.success) {
                this.setWishlists(data.wishlists);
                this.isLoaded = true;

                // Determine which lists currently have this property
                if (!preservePendingInput) {
                    this.pendingWishlists = this.wishlists
                        .filter((list) =>
                            list.listingIds.includes(
                                parseInt(this.listingId, 10)
                            )
                        )
                        .map((list) => list.uid);
                }
            }
        } catch (e) {
            console.error('Failed to load wishlists', e);
            this.error = "We couldn't load your collections. Please try again.";
        } finally {
            this.isLoading = false;
        }
    },

    toggleList(uid) {
        if (this.pendingWishlists.includes(uid)) {
            this.pendingWishlists = this.pendingWishlists.filter(
                (id) => id !== uid
            );
        } else {
            this.pendingWishlists.push(uid);
        }
    },

    async save() {
        if (!this.listingId || this.isSaving) return;
        this.isSaving = true;
        this.error = '';

        try {
            // Refresh before protected mutations so an expired session reaches
            // the structured auth response instead of stale-CSRF validation.
            const csrf = await getCsrfToken(true);
            if (!csrf) {
                console.error('CSRF token not available');
                this.error =
                    "We couldn't verify your request. Please refresh the page and try again.";
                return;
            }

            const formData = new FormData();
            formData.append('listingId', this.listingId);
            formData.append(csrf.name, csrf.value);

            // Append selected wishlists
            // Note: Sending as array requires recursive appending or comma string if backend handles it
            // Our controller handles comma separated string for robustness
            formData.append('wishlistUids', this.pendingWishlists.join(','));

            if (this.newCollectionName.trim()) {
                formData.append(
                    'newWishlistTitle',
                    this.newCollectionName.trim()
                );
            }

            const response = await fetch('/actions/avari/wishlist/update', {
                method: 'POST',
                body: formData,
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'same-origin'
            });

            const data = await readJsonResponse(response);

            if (
                handleAuthenticationRequiredResponse(response, data, {
                    userStore: Alpine.store('user'),
                    modalsStore: Alpine.store('modals')
                })
            ) {
                // Preserve input for one explicit reopen, but never replay the
                // wishlist mutation automatically after login.
                this.retryListingId = parseInt(this.listingId, 10);
                this.isLoaded = false;
                return;
            }

            if (!response.ok) {
                throw new Error(`HTTP error ${response.status}`);
            }

            if (!data) {
                throw new Error('Wishlist API returned an invalid response');
            }

            if (data.success) {
                // Update global wishlist state (red hearts)
                if (
                    Alpine.store('wishlist') &&
                    Array.isArray(data.allDetails)
                ) {
                    // Update the global list of liked IDs based on the response
                    Alpine.store('wishlist').items = data.allDetails.map((id) =>
                        parseInt(id)
                    );
                }

                // Refresh wishlists cache (new collections may have been created)
                this.isLoaded = false;
                this.loadWishlists();

                if (Alpine.store('modals')) {
                    // Close using element to ensure compatibility
                    const modal = document.getElementById(
                        'add-to-wishlist-modal'
                    );
                    if (modal) {
                        Alpine.store('modals').close(modal);
                    } else {
                        // Fallback to string ID
                        Alpine.store('modals').close('add-to-wishlist-modal');
                    }
                }

                // Clear state
                this.newCollectionName = '';
                this.retryListingId = null;
            } else {
                console.error('Failed to save wishlist', data.error);
                this.error =
                    data.error ||
                    "We couldn't update your wishlist. Please try again.";
                if (Alpine.store('modals')) {
                    Alpine.store('modals').markAsError('add-to-wishlist-modal');
                }
            }
        } catch (e) {
            console.error('Error saving wishlist', e);
            this.error = "We couldn't update your wishlist. Please try again.";
            if (Alpine.store('modals')) {
                Alpine.store('modals').markAsError('add-to-wishlist-modal');
            }
        } finally {
            this.isSaving = false;
        }
    }
});
